<template>
  <section class="about-wish-list">
    <div class="about-wish-list__content">
      <AboutPageHeadline
        :title="`ほしいものリストを\n活用しよう`"
        :sub-title="`相手が好きなもの、\n今ほしいと思っているものをスムーズに贈れます`"
      />
      <div class="about-wish-list__steps-area">
        <div class="about-wish-list__white-area">
          <button class="about-wish-list__navigation--prev">
            <ChevronBack />
          </button>
          <swiper-container
            :modules="[Navigation, Pagination]"
            :slides-per-view="1"
            :space-between="16"
            :breakpoints="{
              1024: {
                slidesPerView: 4,
                spaceBetween: 24
              }
            }"
            :navigation="swiperNavigation"
            :pagination="swiperPagination"
            :loop="true"
            class="step-swiper"
          >
            <swiper-slide v-for="(step, index) in steps" :key="index">
              <div class="about-wish-list__step">
                <h3 class="about-wish-list__step-header">
                  <span>{{ step.label1 }}</span>
                  <span>{{ step.label2 }}</span>
                </h3>
                <ImgLazy
                  :image-url="step.src"
                  alt-text=""
                  aspect-ratio="258 / 287"
                  image-class="about-wish-list__step-image"
                />
              </div>
            </swiper-slide>
          </swiper-container>
          <button class="about-wish-list__navigation--next">
            <ChevronForward />
          </button>
        </div>
        <div class="about-wish-list__dots" />
      </div>
      <AboutPageSeeMoreButton
        text="使い方をくわしく見る"
        href="/announcements/472"
      />
    </div>
  </section>
</template>

<script setup>
import ImgLazy from '../../../atoms/images/ImgLazy.vue'
import AboutPageHeadline from '../../../atoms/headlines/AboutPageHeadline.vue'
import ChevronBack from '../../../atoms/svg_icons/ChevronBack.vue'
import ChevronForward from '../../../atoms/svg_icons/ChevronForward.vue'
import AboutPageSeeMoreButton from '../../../molecules/buttons/AboutPageSeeMoreButton.vue'
import { ref, onMounted } from 'vue'
import { Navigation, Pagination } from 'swiper/modules'
import { register } from 'swiper/element/bundle'

register()

const steps = ref([
  {
    label1: 'ギフトを贈りたい相手の',
    label2: 'プロフィール画面からフォロー'
  },
  {
    label1: 'ほしいものリストから',
    label2: 'ギフトを選んで購入'
  },
  {
    label1: '相手に通知が届き',
    label2: '受け取りが完了'
  },
  {
    label1: 'ほしいものリスト以外の',
    label2: 'ギフトも贈れます'
  }
])
const swiperNavigation = {
  prevEl: '.about-wish-list__navigation--prev',
  nextEl: '.about-wish-list__navigation--next'
}
const swiperPagination = {
  el: '.about-wish-list__dots',
  clickable: true
}

onMounted(() => {
  const images = import.meta.glob(
    '../../../../../../images/abouts/wish_list/*',
    {
      eager: true
    }
  )
  Object.entries(images).forEach(([, module], index) => {
    steps.value[index]['src'] = module.default
  })
})
</script>

<style lang="scss" scoped>
.about-wish-list {
  background-color: $gray-3;
  display: flex;
  justify-content: center;

  &__content {
    padding: 96px 72px;
    display: flex;
    flex-direction: column;
    gap: 48px;
    max-width: 1440px;
    width: 100%;

    @media all and (max-width: ($pc-width - 1)) {
      padding: 64px 24px;
      gap: 24px;
    }
  }

  &__steps-area {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__white-area {
    background-color: $white;
    padding-top: 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 32px;

    @media all and (min-width: ($pc-width)) {
      padding: 32px 32px 0 32px;
      justify-content: center;
    }
  }

  &__navigation {
    &--prev,
    &--next {
      width: 48px;
      height: 48px;
      fill: $gray-1;
      cursor: pointer;
      flex-shrink: 0;

      @media all and (min-width: ($pc-width)) {
        display: none;
      }
    }
  }

  &__steps {
    @media all and (min-width: ($pc-width)) {
      display: flex;
      gap: 24px;
      align-items: flex-end;
    }
  }

  &__step {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;

    @media all and (min-width: ($pc-width)) {
      display: flex !important; // v-showでdisplay: noneを設定しているcurrentStep以外のstepを表示するため、importantを設定。
    }

    &-header {
      font-weight: bold;
      font-size: 1.4rem;
      line-height: 1.6;
      text-align: center;
      color: $primary;

      > span {
        display: block;
      }
    }

    &-image {
      max-width: 100%;
      height: auto;
      object-fit: contain;
      padding: 0 16px;

      @media all and (max-width: ($pc-width - 1)) {
        max-width: 248px;
        width: 100%;
      }
    }
  }

  &__dots {
    display: flex;
    gap: 8px;
    justify-content: center;

    @media all and (min-width: ($pc-width)) {
      display: none;
    }
  }
}

swiper-container {
  // swiperでpaddingを考慮したwidthが設定されないため、.about-wish-list__contentと.about-wish-list__white-areaのpaddingを画面サイズから引く
  width: calc(100vw - 144px - 68px);
  // about-wish-list__contentのmax-widthを超えないようにするため、max-widthから.about-wish-list__white-areaのpaddingを引く
  max-width: calc(1440px - 68px);

  @media all and (max-width: ($pc-width - 1)) {
    // PC以外の場合、.about-wish-list__white-areaの左右のpaddingが存在しない代わりに、左右の.about-wish-list__navigationのwidthを引く。
    width: calc(100vw - 48px - 96px);
  }
}

swiper-container::part(wrapper) {
  display: flex;
  align-items: flex-end;
}

:deep(.swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: $gray-2;
}

:deep(.swiper-pagination-bullet-active) {
  background-color: $gray-1;
}
</style>
